import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
    Autocomplete,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Modal,
    TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/vi";
import { saveAs } from "file-saver";
import { useCallback, useEffect, useRef, useState } from "react";
import apiMethods from "../../../utils/api";
import { error } from "../../../utils/toast";
import {
    DateGrid,
    FieldLabel,
    FieldLabelRow,
    FooterActions,
    FooterHint,
    Guidance,
    HeaderCopy,
    HeaderIcon,
    HeaderIdentity,
    MaterialSection,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalShell,
    ModalSubtitle,
    ModalTitle,
    SectionHeading,
    SelectionCount,
    SetupPanel
} from "../../../widgets/MaterialList/Title/MaterialStockCardModal/Styled";
import StockCardPreview from "./StockCardPreview";
import { StockCard, StockCardPreviewResponse } from "./types";

interface StockCardItem {
    id: string;
    name: string;
}
interface ListResponse<T> {
    results?: T[];
    next?: string | null;
}
export interface StockCardModalProps<T extends StockCardItem> {
    open: boolean;
    onClose: () => void;
    itemLabel: string;
    itemLabelPlural: string;
    itemListUrl: string;
    stockCardUrl: string;
    itemIdsPayloadKey: string;
    getItemCode: (item: T) => string;
    filenamePrefix?: string;
}
const PAGE_SIZE = 50;

type RawStockCard = StockCard & { material_code?: string; product_code?: string };
type StockCardPreviewApiResponse = StockCardPreviewResponse | RawStockCard[];

const normalizePreview = (
    response: StockCardPreviewApiResponse
): StockCardPreviewResponse | null => {
    const cards: RawStockCard[] | undefined = Array.isArray(response) ? response : response?.cards;
    if (!cards) return null;
    return {
        cards: cards.map((card) => ({
            ...card,
            item_code: card.item_code || card.material_code || card.product_code || "",
        })),
    };
};

const StockCardModal = <T extends StockCardItem>({
    open,
    onClose,
    itemLabel,
    itemLabelPlural,
    itemListUrl,
    stockCardUrl,
    itemIdsPayloadKey,
    getItemCode,
    filenamePrefix = "the_kho",
}: StockCardModalProps<T>) => {
    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf("month"));
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
    const [selectedItems, setSelectedItems] = useState<T[]>([]);
    const [options, setOptions] = useState<T[]>([]);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isExporting, setIsExporting] = useState<"pdf" | "excel" | null>(null);
    const [preview, setPreview] = useState<StockCardPreviewResponse | null>(null);
    const [step, setStep] = useState<"setup" | "preview">("setup");
    const requestSequence = useRef(0);
    const fetchItems = useCallback(
        async (query: string, targetPage: number, append: boolean) => {
            const sequence = ++requestSequence.current;
            setIsLoadingItems(true);
            try {
                const response = (await apiMethods.get(itemListUrl, {
                    search: query || undefined,
                    page: targetPage,
                    page_size: PAGE_SIZE,
                    ordering: "name",
                })) as ListResponse<T>;
                if (sequence !== requestSequence.current) return;
                const incoming = response?.results || [];
                setOptions((current) =>
                    append
                        ? Array.from(
                              new Map(
                                  [...current, ...incoming].map((item) => [item.id, item])
                              ).values()
                          )
                        : incoming
                );
                setPage(targetPage);
                setHasMore(Boolean(response?.next));
            } catch (requestError) {
                if (sequence === requestSequence.current)
                    error(`Không thể tải danh sách ${itemLabelPlural}`, requestError);
            } finally {
                if (sequence === requestSequence.current) setIsLoadingItems(false);
            }
        },
        [itemLabelPlural, itemListUrl]
    );
    useEffect(() => {
        if (!open || step !== "setup") return;
        const timer = window.setTimeout(() => fetchItems(searchText.trim(), 1, false), 250);
        return () => window.clearTimeout(timer);
    }, [fetchItems, open, searchText, step]);
    const hasInvalidRange = Boolean(startDate && endDate && endDate.isBefore(startDate, "day"));
    const canGenerate = Boolean(
        startDate && endDate && !hasInvalidRange && selectedItems.length && !isGenerating
    );
    const requestPayload = () => ({
        start_date: startDate?.format("YYYY-MM-DD"),
        end_date: endDate?.format("YYYY-MM-DD"),
        [itemIdsPayloadKey]: selectedItems.map((item) => item.id),
    });
    const handleGenerate = async () => {
        if (!canGenerate) return;
        setIsGenerating(true);
        try {
            const response = (await apiMethods.post(
                `${stockCardUrl}preview/`,
                requestPayload()
            )) as StockCardPreviewApiResponse;
            const normalizedPreview = normalizePreview(response);
            if (!normalizedPreview) throw new Error("Dữ liệu xem trước thẻ kho không hợp lệ.");
            setPreview(normalizedPreview);
            setStep("preview");
        } catch (requestError) {
            error("Không thể tạo thẻ kho", requestError);
        } finally {
            setIsGenerating(false);
        }
    };
    const handleDownload = async (format: "pdf" | "excel") => {
        if (!startDate || !endDate) return;
        setIsExporting(format);
        try {
            const url = format === "pdf" ? stockCardUrl : `${stockCardUrl}export-excel/`;
            const blob = format === "excel"
                ? await apiMethods.postBlob(
                    url,
                    requestPayload(),
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json"
                )
                : await apiMethods.postBlob(url, requestPayload());
            saveAs(
                blob,
                `${filenamePrefix}_${startDate.format("YYYYMMDD")}_${endDate.format("YYYYMMDD")}.${format === "pdf" ? "pdf" : "xlsx"}`
            );
        } catch (requestError) {
            error(`Không thể xuất ${format === "pdf" ? "PDF" : "Excel"}`, requestError);
        } finally {
            setIsExporting(null);
        }
    };
    const handleClose = () => {
        setPreview(null);
        setStep("setup");
        onClose();
    };
    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="stock-card-title">
            <ModalShell data-testid="stock-card-modal">
                <ModalHeader>
                    <HeaderIdentity>
                        <HeaderIcon>
                            <PictureAsPdfOutlinedIcon fontSize="small" />
                        </HeaderIcon>
                        <HeaderCopy>
                            <ModalTitle id="stock-card-title">
                                {step === "setup" ? "Tạo thẻ kho" : "Xem trước thẻ kho"}
                            </ModalTitle>
                            <ModalSubtitle>
                                {step === "setup"
                                    ? `Chọn kỳ báo cáo và ${itemLabelPlural} cần đối chiếu nhập - xuất - tồn.`
                                    : `${selectedItems.length} ${itemLabelPlural} · ${startDate?.format("DD/MM/YYYY")} - ${endDate?.format("DD/MM/YYYY")}`}
                            </ModalSubtitle>
                        </HeaderCopy>
                    </HeaderIdentity>
                    <IconButton aria-label="Đóng" onClick={handleClose} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </ModalHeader>
                <ModalBody>
                    {step === "setup" ? (
                        <SetupPanel>
                            <SectionHeading>Khoảng thời gian</SectionHeading>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
                                <DateGrid>
                                    <DatePicker
                                        label="Từ ngày"
                                        value={startDate}
                                        onChange={setStartDate}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: { size: "small", fullWidth: true },
                                        }}
                                    />
                                    <DatePicker
                                        label="Đến ngày"
                                        value={endDate}
                                        onChange={setEndDate}
                                        format="DD/MM/YYYY"
                                        minDate={startDate || undefined}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                fullWidth: true,
                                                error: hasInvalidRange,
                                                helperText: hasInvalidRange
                                                    ? "Ngày kết thúc phải bằng hoặc sau ngày bắt đầu."
                                                    : " ",
                                            },
                                        }}
                                    />
                                </DateGrid>
                            </LocalizationProvider>
                            <MaterialSection>
                                <FieldLabelRow>
                                    <FieldLabel>
                                        {itemLabelPlural.charAt(0).toUpperCase() +
                                            itemLabelPlural.slice(1)}{" "}
                                        trong thẻ kho
                                    </FieldLabel>
                                    <SelectionCount>
                                        {selectedItems.length} {itemLabelPlural} đã chọn
                                    </SelectionCount>
                                </FieldLabelRow>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    filterOptions={(availableOptions) => availableOptions}
                                    options={options}
                                    value={selectedItems}
                                    loading={isLoadingItems}
                                    inputValue={searchText}
                                    onInputChange={(_, value, reason) => {
                                        if (reason === "input" || reason === "clear")
                                            setSearchText(value);
                                    }}
                                    onChange={(_, value) => setSelectedItems(value)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={(option) =>
                                        `${getItemCode(option) || option.id} - ${option.name}`
                                    }
                                    noOptionsText={`Không tìm thấy ${itemLabel}`}
                                    loadingText={`Đang tải ${itemLabelPlural}...`}
                                    ListboxProps={{
                                        onScroll: (event) => {
                                            const listbox = event.currentTarget;
                                            if (
                                                !isLoadingItems &&
                                                hasMore &&
                                                listbox.scrollTop + listbox.clientHeight >=
                                                    listbox.scrollHeight - 24
                                            )
                                                fetchItems(searchText.trim(), page + 1, true);
                                        },
                                    }}
                                    renderTags={(value, getTagProps) =>
                                        value.map((item, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={item.id}
                                                size="small"
                                                label={`${getItemCode(item) || "--"} · ${item.name}`}
                                                sx={{ maxWidth: 260 }}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={`Tìm và chọn một hoặc nhiều ${itemLabelPlural}`}
                                            placeholder={
                                                selectedItems.length
                                                    ? `Tìm thêm ${itemLabelPlural}`
                                                    : `Nhập mã hoặc tên ${itemLabel}`
                                            }
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {isLoadingItems ? (
                                                            <CircularProgress
                                                                color="inherit"
                                                                size={18}
                                                            />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Guidance>
                                    Thẻ kho dùng tồn thực tế tại thời điểm lập báo cáo.
                                </Guidance>
                            </MaterialSection>
                        </SetupPanel>
                    ) : (
                        <>
                            {preview && <StockCardPreview cards={preview.cards} />}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <FooterHint>
                        {step === "setup"
                            ? "Dữ liệu chỉ được tải khi bạn tạo thẻ kho."
                            : "Bấm số chứng từ để mở chứng từ gốc khi hệ thống cung cấp liên kết."}
                    </FooterHint>
                    <FooterActions>
                        {step === "preview" && (
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => setStep("setup")}
                            >
                                Quay lại
                            </Button>
                        )}
                        <Button variant="outlined" onClick={handleClose}>
                            Đóng
                        </Button>
                        {step === "setup" ? (
                            <Button
                                variant="contained"
                                onClick={handleGenerate}
                                disabled={!canGenerate}
                                startIcon={
                                    isGenerating ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <PictureAsPdfOutlinedIcon />
                                    )
                                }
                                data-testid="generate-stock-card"
                            >
                                {isGenerating ? "Đang tạo" : "Tạo thẻ kho"}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={
                                        isExporting === "excel" ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <DownloadOutlinedIcon />
                                        )
                                    }
                                    disabled={Boolean(isExporting)}
                                    onClick={() => handleDownload("excel")}
                                >
                                    Xuất Excel
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={
                                        isExporting === "pdf" ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <DownloadOutlinedIcon />
                                        )
                                    }
                                    disabled={Boolean(isExporting)}
                                    onClick={() => handleDownload("pdf")}
                                >
                                    Tải PDF
                                </Button>
                            </>
                        )}
                    </FooterActions>
                </ModalFooter>
            </ModalShell>
        </Modal>
    );
};
export default StockCardModal;
